import React from "react";
import { AtSign, Calendar, Eye, User, MessageSquare, Paperclip } from "lucide-react";
import { FileItem } from "../../../components/FileItem.tsx";
import { SecureLink } from "../../../api/ApiTypes.ts";
import { formatDateTime } from "../../../utils/dateUtils.ts";
import { FieldDisplay } from "../../../components/FieldDisplay.tsx";
import { MetadataRow } from "../../../components/MetaDataRow.tsx";

interface SecureLinkDetailProps {
    secureLink: SecureLink;
}

export const SecureLinkDetail: React.FC<SecureLinkDetailProps> = ({ secureLink }) => {
    return (
        <div className="space-y-6">
            {/* Header Card */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                <div className="flex justify-between items-start flex-wrap gap-4 mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800">{secureLink.subject}</h1>
                        <p className="text-sm text-slate-500 mt-1">File Number: {secureLink.usaFileNumber}</p>
                    </div>
                    <div className="bg-blue-50 px-3 py-1 rounded-md text-blue-700 text-sm font-medium">
                        ID: {secureLink.id}
                    </div>
                </div>

                <div className="border-b border-slate-100 pb-4 mb-4">
                    <MetadataRow
                        icon={<Calendar className="h-5 w-5 text-blue-500" />}
                        label="Sent:"
                        value={formatDateTime(secureLink.sentDateTime)}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    <FieldDisplay
                        label="Sender"
                        value={secureLink.senderName}
                        icon={<User className="h-5 w-5 text-blue-500" />}
                    />
                    <FieldDisplay
                        label="Recipient"
                        value={secureLink.recipientEmail}
                        icon={<AtSign className="h-5 w-5 text-blue-500" />}
                    />

                    {secureLink.viewDateTime && (
                        <FieldDisplay
                            label="Viewed"
                            value={formatDateTime(secureLink.viewDateTime)}
                            icon={<Eye className="h-5 w-5 text-blue-500" />}
                        />
                    )}
                </div>

                {secureLink.message && (
                    <div className="mt-6 pt-6 border-t border-slate-100">
                        <h2 className="text-lg font-medium text-slate-800 mb-2 flex items-center">
                            <MessageSquare className="h-5 w-5 text-blue-500 mr-2" />
                            Message
                        </h2>
                        <div className="bg-slate-50 p-4 rounded-md text-slate-700 whitespace-pre-line border border-slate-100">
                            {secureLink.message}
                        </div>
                    </div>
                )}
            </div>

            {/* Attachments Section */}
            {secureLink.attachments && secureLink.attachments.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                    <h2 className="text-lg font-medium text-slate-800 mb-4 flex items-center">
                        <Paperclip className="h-5 w-5 text-blue-500 mr-2" />
                        Attachments
                        <span className="ml-2 bg-blue-100 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full">
                            {secureLink.attachments.length}
                        </span>
                    </h2>
                    <div className="space-y-3">
                        {secureLink.attachments.map((file) => (
                            <FileItem key={file.id} file={file} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};